import React from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { usePermissionCheck } from '../../hooks/permissionHooks/usePermissionCheck';
import { PERMISSIONS } from '../../config/permissions';
import { getPartnerPermissions } from '../../utils/partnersPermissions';

const PartnersPermissionTest = () => {
  const user = useSelector((state) => state.user);
  const { 
    canPerform, 
    isAuthenticated,
    isMainVendor 
  } = usePermissionCheck();

  const partnerPermissions = getPartnerPermissions(user.userPermissions, isMainVendor);

  const permissionTests = [
    {
      name: 'View Partners',
      permission: PERMISSIONS.ACTIONS.VIEW_PARTNERS,
      module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
      result: partnerPermissions.canView
    },
    {
      name: 'Create Partners',
      permission: PERMISSIONS.ACTIONS.CREATE_PARTNER,
      module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
      result: partnerPermissions.canCreate
    },
    {
      name: 'Edit Partners',
      permission: PERMISSIONS.ACTIONS.EDIT_PARTNER,
      module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
      result: partnerPermissions.canEdit
    },
    {
      name: 'Delete Partners',
      permission: PERMISSIONS.ACTIONS.DELETE_PARTNER,
      module: PERMISSIONS.MODULES.PARTNER_MANAGEMENT,
      result: partnerPermissions.canDelete
    }
  ];

  if (!isAuthenticated) {
    return (
      <div className="p-6 bg-red-50 rounded-lg border border-red-200">
        <div className="flex gap-2 items-center text-red-800">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Not Authenticated</span>
        </div>
        <p className="mt-2 text-sm text-red-600">
          User is not authenticated. Cannot test permissions.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="flex gap-2 items-center mb-4">
        <Shield className="w-6 h-6 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">Partners Permission Test</h3>
      </div>

      {/* User Info */}
      <div className="p-4 mb-6 bg-gray-50 rounded-lg">
        <h4 className="mb-2 font-medium text-gray-900">User Information</h4>
        <div className="space-y-1 text-sm text-gray-600">
          <p><strong>Authenticated:</strong> {isAuthenticated ? 'Yes' : 'No'}</p>
          <p><strong>Main Vendor:</strong> {isMainVendor ? 'Yes' : 'No'}</p>
          <p><strong>User Role:</strong> {user.userRole || 'Not set'}</p>
          <p><strong>Has Any Partner Permission:</strong> {partnerPermissions.hasAny ? 'Yes' : 'No'}</p>
        </div>
      </div>

      {/* Permission Tests */}
      <div className="space-y-3">
        <h4 className="font-medium text-gray-900">Permission Tests</h4>
        {permissionTests.map((test, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`p-3 rounded-lg border ${
              test.result 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}
          >
            <div className="flex gap-3 justify-between items-center">
              <div className="flex gap-2 items-center">
                {test.result ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <div>
                  <p className="font-medium text-gray-900">{test.name}</p>
                  <p className="text-sm text-gray-600">
                    Module: {test.module} | Action: {test.permission}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  test.result 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {test.result ? 'GRANTED' : 'DENIED'}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Raw Permissions Data */}
      <div className="p-4 mt-6 bg-gray-50 rounded-lg">
        <h4 className="mb-2 font-medium text-gray-900">Raw Permissions Data</h4>
        <div className="text-sm text-gray-600">
          <pre className="overflow-auto whitespace-pre-wrap">
            {JSON.stringify(user.userPermissions, null, 2)}
          </pre>
        </div>
      </div>

      {/* Recommendations */}
      <div className="p-4 mt-6 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex gap-2 items-center mb-2">
          <AlertTriangle className="w-5 h-5 text-blue-600" />
          <h4 className="font-medium text-blue-900">Recommendations</h4>
        </div>
        <ul className="space-y-1 text-sm text-blue-800">
          {!partnerPermissions.hasAny && (
            <li>• User has no partner permissions. Consider granting at least View Partners permission.</li>
          )}
          {partnerPermissions.canView && !partnerPermissions.canCreate && (
            <li>• User can view but not create partners. This is a valid read-only setup.</li>
          )}
          {partnerPermissions.canCreate && !partnerPermissions.canEdit && (
            <li>• User can create but not edit partners. Consider if this is the intended behavior.</li>
          )}
          {partnerPermissions.canDelete && !partnerPermissions.canEdit && (
            <li>• User can delete but not edit partners. This might be unintentional.</li>
          )}
          {isMainVendor && (
            <li>• Main vendor has full access to all partner operations.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default PartnersPermissionTest; 